interface SidebarButtonProps extends HTMLAttributes<HTMLDivElement> {
  icon?: IconSymbol;
  onClick?: () => void;
  disabled?: boolean;
  isExpanded?: boolean;
}
import { Icon, type IconSymbol } from "@spz/ui/components/icon";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import SpzLogo from "../assets/spz-logo.svg";
import SupervizorLogo from "../assets/supervizor-logo.svg";
import { cn } from "../lib/utils";
import { Typography } from "./typography";

interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export function Sidebar({ children, className, ...props }: SidebarProps) {
  return (
    <div className="z-30 relative">
      <aside
        className={cn(
          "h-full",
          "transition-all duration-300 ease-in-out",
          "bg-primary",
          className,
        )}
        data-testid="sidebar-container"
        {...props}
      >
        {children}
      </aside>
    </div>
  );
}

export interface SidebarHeaderProps extends HTMLAttributes<HTMLDivElement> {
  closeSidebarText?: string;
  children?: ReactNode;
  onToggle: () => void;
  closeSidebarIcon: IconSymbol;
  isExpanded?: boolean;
}

export function SidebarHeader({
  className,
  closeSidebarText,
  closeSidebarIcon,
  children,
  onToggle,
  isExpanded = false,
  ...props
}: SidebarHeaderProps) {
  return (
    <div {...props}>
      <div className={cn("flex items-center ", "mx-4", "mt-6")}>
        <div className="flex-shrink-0">
          <img
            src={SpzLogo}
            alt="Spz logo"
            className="object-contain"
            width={32}
            height={32}
            data-testid="sidebar-spz-logo"
          />
        </div>
        <div
          className={cn(
            "ml-2.5 overflow-hidden transition-all duration-300 ease-in-out",
          )}
        >
          <img
            src={SupervizorLogo}
            alt="Supervizor logo"
            className="h-3 w-auto"
            data-testid="sidebar-supervizor-logo"
          />
        </div>
      </div>
      {children}
      <SidebarTrigger
        text={closeSidebarText}
        icon={closeSidebarIcon}
        onTrigger={onToggle}
        isExpanded={isExpanded}
        data-testid="sidebar-close-button"
      />
    </div>
  );
}

export function SidebarContent({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "overflow-y-auto overflow-x-hidden flex gap-1 flex-col",
        "menu-scroll",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface SidebarButtonComponentProps extends SidebarButtonProps {
  ref?: React.Ref<HTMLDivElement>;
}
export const SidebarButton: React.FC<SidebarButtonComponentProps> = ({
  icon,
  children,
  onClick,
  className,
  disabled = false,
  isExpanded = false,
  ref,
  ...props
}) => {
  const clickHandler = disabled ? () => {} : onClick;
  const buttonContent = (
    <div
      ref={ref}
      onClick={clickHandler}
      className={cn(
        "flex items-center font-semibold gap-2 px-2 mx-4 h-6 w-[198px] rounded hover:bg-pink-600 hover:cursor-pointer text-white whitespace-nowrap transition-all duration-300",
        disabled && "cursor-not-allowed opacity-50",
        isExpanded ? "w-[198px]" : "w-6.5",
        className,
      )}
      {...props}
    >
      {icon && (
        <span className="flex items-center">
          <Icon icon={icon} size={20} />
        </span>
      )}
      <span
        className={cn(
          "truncate transition-all duration-300 ease-in-out font-normal",
          isExpanded ? "max-w-[198px] opacity-100" : "max-w-0 opacity-0",
        )}
      >
        {children}
      </span>
    </div>
  );
  return buttonContent;
};

interface SidebarItemProps
  extends Omit<HTMLAttributes<HTMLAnchorElement>, "href"> {
  icon?: IconSymbol;
  to?: string;
  active?: boolean;
  isExpanded?: boolean;
  onClick?: () => void;
}

interface SidebarItemComponentProps extends SidebarItemProps {
  ref?: React.Ref<HTMLAnchorElement>;
}
export const SidebarItem: React.FC<SidebarItemComponentProps> = ({
  icon,
  children,
  to = "#",
  active: forcedActive,
  onClick,
  className,
  isExpanded = false,
  ref,
  ...props
}) => {
  const location = useLocation();
  const isActive = forcedActive ?? location.pathname === to;

  return (
    <Link
      ref={ref}
      to={to}
      onClick={onClick}
      className={cn(
        "flex items-center font-semibold gap-2 px-2 mx-4 h-6 w-[198px] rounded hover:bg-pink-600 hover:cursor-pointer text-white whitespace-nowrap transition-all duration-300",
        isActive && "bg-pink-700",
        isExpanded ? "w-[198px]" : "w-6.5",
        className,
      )}
      {...props}
    >
      {icon && (
        <span className="flex items-center">
          <Icon
            icon={icon}
            size={20}
            className={cn(
              "shrink-0",
              isActive ? "text-white" : "text-muted-foreground",
            )}
          />
        </span>
      )}
      <span
        className={cn(
          "truncate transition-all duration-300 ease-in-out font-normal",
          isExpanded ? "max-w-[198px] opacity-100" : "max-w-0 opacity-0",
        )}
      >
        {children}
      </span>
    </Link>
  );
};

export function SidebarFooter({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("mb-6 space-y-1", className)} {...props}>
      {children}
    </div>
  );
}

interface SidebarTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  text?: string;
  onTrigger: () => void;
  icon: IconSymbol;
  isExpanded?: boolean;
  "data-testid"?: string;
}
export function SidebarTrigger({
  text = "Close menu",
  onTrigger,
  icon,
  className,
  isExpanded = false,
  "data-testid": dataTestId,
}: SidebarTriggerProps) {
  return (
    <div
      className={cn(
        "flex items-center font-semibold gap-2 px-2 mx-4 h-6 w-[198px] rounded hover:bg-pink-600 hover:cursor-pointer text-white whitespace-nowrap transition-all duration-300 my-5.5",
        isExpanded ? "w-[198px]" : "w-6.5",
        className,
      )}
      onClick={onTrigger}
      onKeyUp={(e) => e.key === "Enter" && onTrigger()}
      onKeyDown={(e) => e.key === " " && onTrigger()}
      data-testid={dataTestId}
    >
      <div className="flex items-center w-full gap-2">
        <Icon icon={icon} size={20} className="shrink-0" />
        <Typography
          variant={"body"}
          trans={text}
          className={cn(
            "truncate transition-all duration-300 ease-in-out font-normal",
            isExpanded ? "max-w-[198px] opacity-100" : "max-w-0 opacity-0",
          )}
        />
      </div>
    </div>
  );
}

interface SidebarUserProfileProps extends HTMLAttributes<HTMLDivElement> {
  name: string;
  email?: string;
  avatarUrl?: string;
  onClick?: () => void;
  isExpanded?: boolean;
}

interface SidebarUserProfileComponentProps extends SidebarUserProfileProps {
  ref?: React.Ref<HTMLDivElement>;
}
export const SidebarUserProfile: React.FC<SidebarUserProfileComponentProps> = ({
  name,
  email,
  avatarUrl,
  onClick,
  className,
  isExpanded = false,
  ref,
  ...props
}) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center font-semibold gap-2 px-2 mx-4 h-6 w-[198px] rounded hover:bg-pink-600 hover:cursor-pointer text-white whitespace-nowrap transition-all duration-300",
        isExpanded ? "w-[198px]" : "w-6.5",
        className,
      )}
      onClick={onClick}
      {...props}
    >
      <div className="flex items-center">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name}
            className="h-full w-full object-cover"
          />
        ) : (
          <Icon icon="user" size={20} />
        )}
      </div>
      {(name || email) && (
        <div
          className={cn(
            "truncate transition-all duration-300 ease-in-out font-normal",
            isExpanded ? "max-w-[198px] opacity-100" : "max-w-0 opacity-0",
          )}
        >
          {name && (
            <Typography variant="body" className="font-normal">
              {name}
            </Typography>
          )}
          {email && (
            <Typography variant="body" className="font-normal">
              {email}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
